import { AHText, Box, ColorType } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";

import { View } from "react-native";
import React, { useState, useDeferredValue } from "react";

import { useWorkspaceId } from "../workspaceId";

import {
  WorkspaceDetailNavigationHeaderResponsive,
} from "../WorkspaceDetailNavigationHeader";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import {
  AnalyticsTimeResolution,
  WorkspaceAnalyticsDimensionType,
} from "./__generated__/WorkspaceAnalyticsDetailQuery.graphql";
import { WorkspaceAnalyticsStackScreenProps } from "./WorkspaceAnalyticsStack";
import { HeaderButton } from "@/src/components";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { WorkspaceAnalyticsSinglePeriodDetailQuery } from "./__generated__/WorkspaceAnalyticsSinglePeriodDetailQuery.graphql";
import { SinglePeriodTable } from "./SinglePeriodTable";
import { OrgSearchInput } from "../OrgSearchInput";
import ParentSize from "@visx/responsive/lib/components/ParentSize";

export function WorkspaceAnalyticsSinglePeriodDetailScreen(
  props: WorkspaceAnalyticsStackScreenProps<"WorkspaceAnalyticsSinglePeriodDetail">
) {
  const wId = useWorkspaceId();

  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <WorkspaceAnalyticsScreenComponent
          workspaceId={wId}
          fetchKey={fk}
          onBack={() => {
            props.navigation.goBack();
          }}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WorkspaceAnalyticsScreenComponent({
  fetchKey,
  workspaceId,

  onBack,
}: {
  fetchKey: string;
  itemActionComponent?: React.ReactNode;

  workspaceId: string;
  color?: ColorType;

  onBack: () => void;
}) {
  const [fk,] = useState(0);
  const intl_tokens = useIntlTokens();

  const [groupByDimension, setGroupByDimensio] =
    useState<WorkspaceAnalyticsDimensionType>("STORE");
  const [timeResolution, setTimeResolution] =
    useState<AnalyticsTimeResolution>("WEEK");

  const deferredTimeResolution = useDeferredValue(timeResolution);
  const deferredGroupByDimension = useDeferredValue(groupByDimension);
  const data = useLazyLoadQuery<WorkspaceAnalyticsSinglePeriodDetailQuery>(
    graphql`
      query WorkspaceAnalyticsSinglePeriodDetailQuery(
        $workspaceId: ID!
        $groupByDimension: WorkspaceAnalyticsDimensionType!
        $timeResolution: AnalyticsTimeResolution!
      ) {
        workspace(id: $workspaceId) @required(action: THROW) {
          singlePeriodAnalytics(
            groupByDimension: $groupByDimension
            timeResolution: $timeResolution
          ) {
            ...SinglePeriodTable
          }
        }
      }
    `,
    {
      workspaceId,

      groupByDimension: deferredGroupByDimension,
      timeResolution: deferredTimeResolution,
    },
    {
      fetchKey: fetchKey + fk,
      fetchPolicy: "store-and-network",
    }
  );
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  if (!data.workspace.singlePeriodAnalytics) {
    return null;
  }
  return (
    <View style={{ flex: 1 }}>
      <WorkspaceDetailNavigationHeaderResponsive
        left={<HeaderButton type="WEB-BACK" onPress={onBack} />}
        right={null}
      />
      <Box
        style={{ alignItems: "center", flex: 1, width: "100%" }}
        spaceTop="large"
        spaceBottom="medium"
        spaceHorizontal="large"
      >
        <Box
          cornerRadius="large"
          style={{ overflow: "hidden", borderWidth: 1, flex: 1, width: "100%" }}
          borderColor="border-primary"
          color="surface"
        >
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            spaceHorizontal="large"
            spaceVertical="medium"
          >
            <OrgSearchInput
              mode="standalone"
              value={search}
              onChangeText={setSearch}
            />

            <Box style={{ flexDirection: "row" }} gap="medium">
              <SegmentedControlNew
                items={[
                  {
                    children: <AHText type="subtitle">M</AHText>,
                    width: 40,
                    value: "MONTH" satisfies AnalyticsTimeResolution,
                  },
                  {
                    width: 40,
                    children: <AHText type="subtitle">W</AHText>,
                    value: "WEEK" satisfies AnalyticsTimeResolution,
                  },
                ]}
                selected={timeResolution}
                setSelected={(selected) => {
                  setTimeResolution(selected);
                }}
              />
              <SegmentedControlNew
                items={[
                  {
                    children: (
                      <AHText type="subtitle">{intl_tokens.Unit}</AHText>
                    ),
                    width: 100,
                    value: "STORE" satisfies WorkspaceAnalyticsDimensionType,
                  },
                  {
                    width: 100,
                    children: <AHText type="subtitle">Workspace</AHText>,
                    value:
                      "WORKSPACE_OWNER" satisfies WorkspaceAnalyticsDimensionType,
                  },
                ]}
                selected={groupByDimension}
                setSelected={(selected) => {
                  setGroupByDimensio(selected);
                }}
              />
            </Box>
          </Box>
          <View style={{ flex: 1, width: "100%" }}>
            <ParentSize debounceTime={10}>
              {(size) =>
                data.workspace.singlePeriodAnalytics ? (
                  <SinglePeriodTable
                    searchQuery={deferredSearch}
                    data={data.workspace.singlePeriodAnalytics}
                    height={size.height}
                    width={size.width}
                  />
                ) : null
              }
            </ParentSize>
          </View>
        </Box>
      </Box>
    </View>
  );
}
